import React, { useMemo } from 'react';
import { computeStats, type LodgementRecord } from '../../hooks/useLodgements';
import type { TargetProgress } from '../../hooks/useLodgementTargets';
import { money, attainmentChip, EMPTY } from './LodgementDisplay';

/**
 * Per-person summary, the in-app answer to the source workbook's Main sheet.
 *
 * The workbook this feature replaced was a per-person book: one tab each, with
 * targets at the top and a monthly rollup per person. The register alone is the
 * flat data behind that and answers none of the questions the book was kept to
 * answer, so this panel puts the person back on the page.
 *
 * ONE DERIVATION, NOT TWO. Every column comes from `computeStats`, the same
 * function behind the page's headline cards, applied per owner instead of to
 * the whole set. That is deliberate: a second definition of "settled" would let
 * the panel's column disagree with the card directly above it, and a table that
 * does not add up to its own total is worse than no table.
 *
 * COMPLETENESS IS STATED, NEVER ASSUMED. `rowsComplete` says whether the caller
 * managed to page the entire period. When it is false the panel says which
 * subset it covers, because a per-person total that is quietly short is a
 * number someone will act on.
 */

interface Props {
  /** Every row in the period the caller could fetch, not just the visible page. */
  rows: LodgementRecord[];
  rowsComplete: boolean;
  loading: boolean;
  /** Owner ref to display name, from the same option map the picker uses. */
  ownerLabels: Map<string, string>;
  targets: TargetProgress[];
  /**
   * Break each person's month into its four weeks. Only meaningful when the
   * selected period IS one month, so the caller decides rather than this panel
   * guessing from the row dates.
   */
  weeklySplit: boolean;
}

interface WeekCell {
  count: number;
  cents: number;
}

interface OwnerRow {
  ownerId: string;
  label: string;
  lodgements: number;
  lodgedCents: number;
  settledCount: number;
  settledCents: number;
  /** False when no row in the group carries a settled amount at all. */
  hasSettledAmount: boolean;
  conversionPct: number | null;
  medianDaysToFormal: number | null;
  countTargetPct: number | null;
  amountTargetPct: number | null;
  /** Four buckets, 1st to 4th week. Always present; only rendered on request. */
  weeks: WeekCell[];
}

const HEADERS: Array<[string, boolean]> = [
  ['Who', false], ['Lodgements', true], ['$ lodged', true],
  ['Settled', true], ['$ settled', true], ['Conversion', true],
  ['Median to formal', true], ['% of target', false],
];

const WEEK_LABELS = ['1st', '2nd', '3rd', '4th'];

/**
 * WEEKS ARE DAYS 1-7, 8-14, 15-21 AND 22 TO MONTH END. Stated here, and in a
 * tooltip on the column group, because it is a decision rather than a fact.
 *
 * The source workbook's week headings were typed by hand and matched no
 * day-of-month rule at all: tested across 1520 rows, any candidate rule agreed
 * with the sheet on roughly half of them. There was no hidden convention to
 * recover, so the choice was between reproducing an inconsistency and adopting
 * a rule. This is the rule, and being deterministic is the whole point: the
 * same row lands in the same week every time the page is opened.
 *
 * The fourth bucket absorbs days 29 to 31, so it is 7 to 10 days long. That is
 * a deliberate consequence of there being four labelled weeks in a month that
 * does not contain four of them.
 */
function weekIndexOf(isoDate: string): number | null {
  const day = Number(isoDate.slice(8, 10));
  if (!Number.isFinite(day) || day < 1) return null;
  return Math.min(3, Math.floor((day - 1) / 7));
}

const WEEK_RULE = 'Weeks are days 1 to 7, 8 to 14, 15 to 21, and 22 to the end of the month.';

export function LodgementOwnerSummary({
  rows, rowsComplete, loading, ownerLabels, targets, weeklySplit,
}: Props): React.ReactElement | null {
  const summary = useMemo<OwnerRow[]>(() => {
    const byOwner = new Map<string, LodgementRecord[]>();
    for (const r of rows) {
      const key = r.ownerId || 'unattributed';
      const bucket = byOwner.get(key);
      if (bucket) bucket.push(r);
      else byOwner.set(key, [r]);
    }

    /**
     * Only an APPLICABLE target counts. `notApplicable` means the target's own
     * effective window does not overlap the selected period, and showing it
     * would judge a person against a target that did not exist at the time.
     */
    const pctFor = (ownerId: string, type: TargetProgress['type']): number | null => {
      const t = targets.find((p) => p.ownerId === ownerId && p.type === type && !p.notApplicable);
      return t ? t.percent : null;
    };

    const out: OwnerRow[] = [];
    byOwner.forEach((group, ownerId) => {
      const s = computeStats(group);
      const weeks: WeekCell[] = WEEK_LABELS.map(() => ({ count: 0, cents: 0 }));
      for (const r of group) {
        const w = weekIndexOf(r.dateLodged);
        if (w === null) continue;
        weeks[w].count += 1;
        weeks[w].cents += r.lodgeAmountCents || 0;
      }
      out.push({
        ownerId,
        weeks,
        label: ownerLabels.get(ownerId) ?? ownerId,
        lodgements: s.lodgementCount,
        lodgedCents: s.lodgedAmountCents,
        settledCount: s.settledCount,
        settledCents: s.settledAmountCents,
        hasSettledAmount: group.some((r) => r.settledAmountCents != null),
        conversionPct: s.conversionPct,
        medianDaysToFormal: s.medianDaysToFormal,
        countTargetPct: pctFor(ownerId, 'lodgement_count'),
        amountTargetPct: pctFor(ownerId, 'lodgement_amount'),
      });
    });

    return out.sort((a, b) => b.lodgedCents - a.lodgedCents);
  }, [rows, ownerLabels, targets]);

  if (!loading && summary.length === 0) return null;

  return (
    <div className="rounded-lg border border-gray-700 bg-gray-900/40">
      <div className="border-b border-gray-700 px-4 py-3">
        <h2 className="text-base font-semibold text-white">By person</h2>
        <p className="mt-0.5 text-[11px] text-gray-400">
          One row per person with activity in the selected period, ordered by
          value lodged. Target columns appear where a target covers this period.
          {weeklySplit && ` ${WEEK_RULE}`}
        </p>
      </div>

      {!loading && !rowsComplete && (
        <div className="border-b border-gray-700 bg-amber-900/10 px-4 py-2 text-[11px] text-amber-300">
          This summary covers the first {summary.reduce((a, r) => a + r.lodgements, 0)} lodgements
          in the period. There are more rows than could be loaded in one pass, so
          the per-person totals below are understated.
        </div>
      )}

      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr>
              {HEADERS.map(([h, right]) => (
                <th
                  key={h}
                  scope="col"
                  // Spans both header rows so the week group can sit alongside
                  // rather than pushing every other label down a line.
                  rowSpan={weeklySplit ? 2 : undefined}
                  className={`whitespace-nowrap border-b border-gray-700 bg-gray-800 px-3 py-2 align-bottom text-[11px] uppercase tracking-wider text-gray-500 ${right ? 'text-right' : 'text-left'}`}
                >
                  {h}
                </th>
              ))}
              {weeklySplit && (
                <th
                  scope="colgroup"
                  colSpan={WEEK_LABELS.length}
                  title={WEEK_RULE}
                  className="whitespace-nowrap border-b border-l border-gray-700 bg-gray-800 px-3 py-2 text-center text-[11px] uppercase tracking-wider text-gray-500"
                >
                  Weeks lodged
                </th>
              )}
            </tr>
            {weeklySplit && (
              <tr>
                {WEEK_LABELS.map((w, i) => (
                  <th
                    key={w}
                    scope="col"
                    title={WEEK_RULE}
                    className={`whitespace-nowrap border-b border-gray-700 bg-gray-800 px-3 py-2 text-right text-[11px] uppercase tracking-wider text-gray-500 ${i === 0 ? 'border-l' : ''}`}
                  >
                    {w}
                  </th>
                ))}
              </tr>
            )}
          </thead>
          <tbody>
            {loading && (
              <tr>
                <td
                  colSpan={HEADERS.length + (weeklySplit ? WEEK_LABELS.length : 0)}
                  className="px-4 py-6 text-center text-sm text-gray-400"
                >
                  Loading…
                </td>
              </tr>
            )}
            {!loading && summary.map((r) => (
              <tr key={r.ownerId} className="border-b border-gray-800/60">
                <td className="whitespace-nowrap px-3 py-2 text-sm text-white">{r.label}</td>
                <td className="whitespace-nowrap px-3 py-2 text-right text-sm tabular-nums text-white">{r.lodgements}</td>
                <td className="whitespace-nowrap px-3 py-2 text-right text-sm tabular-nums text-white">{money(r.lodgedCents)}</td>
                <td className="whitespace-nowrap px-3 py-2 text-right text-sm tabular-nums text-emerald-300">{r.settledCount}</td>
                <td className="whitespace-nowrap px-3 py-2 text-right text-sm tabular-nums text-emerald-300">
                  {r.hasSettledAmount ? money(r.settledCents) : EMPTY}
                </td>
                <td className="whitespace-nowrap px-3 py-2 text-right text-sm tabular-nums text-amber-300">
                  {r.conversionPct === null ? EMPTY : `${r.conversionPct}%`}
                </td>
                <td className="whitespace-nowrap px-3 py-2 text-right text-sm tabular-nums text-gray-400">
                  {r.medianDaysToFormal === null ? EMPTY : `${r.medianDaysToFormal}d`}
                </td>
                {/*
                  Blank, not an error and not a zero, when nobody set a target
                  for this person. A 0% here would read as "missed the target"
                  when the truth is that no target was ever agreed.
                */}
                <td className="whitespace-nowrap px-3 py-2 text-sm">
                  <div className="flex items-center gap-1.5">
                    {r.countTargetPct !== null && (
                      <span
                        className={`rounded px-1.5 py-0.5 text-[11px] font-bold ${attainmentChip(r.countTargetPct)}`}
                        title="Lodgement count against target"
                      >
                        {r.countTargetPct}% count
                      </span>
                    )}
                    {r.amountTargetPct !== null && (
                      <span
                        className={`rounded px-1.5 py-0.5 text-[11px] font-bold ${attainmentChip(r.amountTargetPct)}`}
                        title="Lodged value against target"
                      >
                        {r.amountTargetPct}% value
                      </span>
                    )}
                  </div>
                </td>
                {/*
                  Count above, value below, in one cell per week. Eight separate
                  columns would double the table's width for the same four
                  facts, and the count is what the eye scans for.
                */}
                {weeklySplit && r.weeks.map((w, i) => (
                  <td
                    key={WEEK_LABELS[i]}
                    className={`whitespace-nowrap px-3 py-2 text-right text-sm tabular-nums ${i === 0 ? 'border-l border-gray-700' : ''}`}
                  >
                    <div className={w.count ? 'text-white' : 'text-gray-600'}>{w.count || EMPTY}</div>
                    <div className="text-[10px] text-gray-500">{w.count ? money(w.cents) : ''}</div>
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
